import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, Search, Save, Copy, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminQuickRegister() {
  const [cnpj, setCnpj] = useState("");
  const [data, setData] = useState(null);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [plan, setPlan] = useState("BRONZE");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)); }, []);

  const lookup = async () => {
    setBusy(true);
    try {
      const digits = cnpj.replace(/\D/g, "");
      const { data } = await api.get(`/admin/cnpj/${digits}`);
      setData(data);
      toast.success(`Encontrado: ${data.nome_fantasia || data.razao_social}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro"); }
    finally { setBusy(false); }
  };

  const register = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/companies/quick", {
        cnpj: cnpj.replace(/\D/g, ""),
        plan, city_slug: city, owner_email: email,
      });
      setResult(res);
      toast.success("Empresa cadastrada!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copiado"); };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Cadastro rápido por CNPJ</h1>
          <p className="text-stone-500 text-sm mt-1">Busque dados na Receita via BrasilAPI e crie a empresa em segundos.</p>
        </div>
        <a href="/admin" className="text-sm text-blue-900 hover:text-blue-950">← Voltar</a>
      </div>

      <Card className="p-6 rounded-2xl border-stone-200 mb-6">
        <Label>CNPJ</Label>
        <div className="flex gap-2 mt-1">
          <Input value={cnpj} onChange={(e) => setCnpj(e.target.value)} placeholder="00.000.000/0000-00" className="rounded-xl font-mono" data-testid="qr-cnpj"/>
          <Button onClick={lookup} disabled={busy || !cnpj} className="bg-stone-900 hover:bg-stone-800 text-white rounded-xl" data-testid="qr-lookup">
            <Search size={14} className="mr-1.5"/> Buscar
          </Button>
        </div>
      </Card>

      {data && (
        <Card className="p-6 rounded-2xl border-blue-200 bg-blue-50 mb-6">
          <div className="flex items-start gap-3">
            <Building2 size={22} className="text-blue-900 shrink-0 mt-0.5"/>
            <div className="text-sm text-blue-950 flex-1 min-w-0">
              <div className="font-display text-lg font-bold">{data.nome_fantasia || data.razao_social}</div>
              <div className="text-xs">{data.razao_social}</div>
              <div className="text-xs mt-2">{data.cnae_fiscal_descricao}</div>
              <div className="text-xs mt-1 text-stone-700">{data.logradouro}, {data.numero} - {data.bairro}, {data.municipio}/{data.uf}</div>
              {data.ddd_telefone_1 && <div className="text-xs">Tel: {data.ddd_telefone_1}</div>}
            </div>
          </div>
        </Card>
      )}

      {data && (
        <Card className="p-6 rounded-2xl border-stone-200 mb-6">
          <h2 className="font-display text-lg font-semibold mb-4">Configuração</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>E-mail do proprietário</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="comerciante@empresa.com" className="rounded-xl mt-1" data-testid="qr-email"/>
              <p className="text-[11px] text-stone-500 mt-1">Se não existir, criaremos uma conta MERCHANT com senha temporária.</p>
            </div>
            <div>
              <Label>Cidade</Label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="qr-city"><SelectValue placeholder="Escolha"/></SelectTrigger>
                <SelectContent>
                  {cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plano inicial</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="qr-plan"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="BRONZE">Bronze</SelectItem>
                  <SelectItem value="PRATA">Prata</SelectItem>
                  <SelectItem value="OURO">Ouro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end mt-5">
            <Button onClick={register} disabled={busy || !city || !email} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="qr-register">
              <Save size={14} className="mr-1.5"/> {busy ? "Cadastrando..." : "Cadastrar empresa"}
            </Button>
          </div>
        </Card>
      )}

      {result && (
        <Card className="p-6 rounded-2xl border-2 border-emerald-400 bg-emerald-50">
          <div className="flex items-start gap-3">
            <CheckCircle size={28} className="text-emerald-700 shrink-0"/>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-lg font-bold text-emerald-900">Empresa cadastrada com sucesso!</h3>
              <p className="text-sm text-emerald-800 mt-1">{result.message}</p>
              <div className="mt-4 space-y-2">
                <div className="bg-white p-3 rounded-xl border border-emerald-200">
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">E-mail do proprietário</div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-mono break-all">{result.owner.email}</span>
                    <button onClick={() => copy(result.owner.email)} className="text-emerald-700 hover:text-emerald-900 shrink-0"><Copy size={14}/></button>
                  </div>
                </div>
                {result.owner.temp_password && (
                  <div className="bg-white p-3 rounded-xl border border-emerald-200">
                    <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Senha temporária</div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-mono">{result.owner.temp_password}</span>
                      <button onClick={() => copy(result.owner.temp_password)} className="text-emerald-700 hover:text-emerald-900"><Copy size={14}/></button>
                    </div>
                  </div>
                )}
                <div className="bg-white p-3 rounded-xl border border-emerald-200">
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Link de onboarding</div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-blue-900 break-all">{result.onboarding_link}</span>
                    <button onClick={() => copy(result.onboarding_link)} className="text-emerald-700 hover:text-emerald-900 shrink-0"><Copy size={14}/></button>
                  </div>
                </div>
              </div>
              <Button onClick={() => { setResult(null); setData(null); setCnpj(""); setEmail(""); setCity(""); }} className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">Cadastrar outra</Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
